"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@domainstack/ui/utils";
const ROTATING_WORDS = ["registration", "DNS records", "hosting", "email", "SEO", "certificates", "headers", "servers", "IP addresses", "geolocation", "sitemaps", "meta tags"];
const INTERVAL_MS = 2400;

// shared by the word swap and the pill resize so both land together
const EASE = [0.22, 1, 0.36, 1] as const;
const DURATION_S = 0.5;
const RESIZE_ANIMATION: KeyframeAnimationOptions = {
  duration: DURATION_S * 1000,
  easing: `cubic-bezier(${EASE.join(", ")})`
};
const HEADING_COMPLETION = "registration, DNS records, hosting, email, SEO, certificates, and more.";
export function HomeHero(t0) {
  const $ = _c(38);
  const {
    className
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);
  const measureRef = useRef(null);
  const [widths, setWidths] = useState(null);
  const width = widths?.[index];
  const headingRef = useRef(null);
  const leadRef = useRef(null);
  const capLeftRef = useRef(null);
  const pillMidRef = useRef(null);
  const capRightRef = useRef(null);
  const trailRef = useRef(null);
  const prevWidthRef = useRef(width);
  let t1;
  let t2;
  if ($[0] !== shouldReduceMotion) {
    t1 = () => {
      if (shouldReduceMotion) {
        return;
      }
      let id;
      const start = () => {
        if (id !== undefined) {
          return;
        }
        id = setInterval(() => {
          if (pausedRef.current) {
            return;
          }
          setIndex(_temp);
        }, INTERVAL_MS);
      };
      const stop = () => {
        if (id === undefined) {
          return;
        }
        clearInterval(id);
        id = undefined;
      };
      const handleVisibilityChange = () => {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      };
      if (!document.hidden) {
        start();
      }
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        stop();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t2 = [shouldReduceMotion];
    $[0] = shouldReduceMotion;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (!measureRef.current) {
        return;
      }
      const mirrors = Array.from(measureRef.current.children);
      let raf = 0;
      const measure = () => {
        const next = mirrors.map(_temp2);
        setWidths(prev_0 => {
          if (prev_0 && prev_0.length === next.length && prev_0.every((w, i) => w === next[i])) {
            return prev_0;
          }
          return next;
        });
      };
      measure();
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(measure);
      });
      for (const mirror_0 of mirrors) {
        ro.observe(mirror_0);
      }
      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
      };
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== shouldReduceMotion || $[6] !== width) {
    t5 = () => {
      const prev_1 = prevWidthRef.current;
      prevWidthRef.current = width;
      if (!prev_1 || !width || prev_1 === width || shouldReduceMotion) {
        return;
      }
      const shift = (width - prev_1) / 2;
      const mid = pillMidRef.current?.offsetWidth ?? 0;
      const prevMid = mid - (width - prev_1);
      const animations = [capLeftRef.current?.animate([{
        transform: `translateX(${shift}px)`
      }, {
        transform: "none"
      }], RESIZE_ANIMATION), capRightRef.current?.animate([{
        transform: `translateX(${-shift}px)`
      }, {
        transform: "none"
      }], RESIZE_ANIMATION), mid > 0 && prevMid > 0 ? pillMidRef.current?.animate([{
        transform: `scaleX(${prevMid / mid})`
      }, {
        transform: "none"
      }], RESIZE_ANIMATION) : undefined, trailRef.current?.animate([{
        transform: `translateX(${-shift}px)`
      }, {
        transform: "none"
      }], RESIZE_ANIMATION)];
      if (headingRef.current && getComputedStyle(headingRef.current).flexDirection === "row") {
        animations.push(leadRef.current?.animate([{
          transform: `translateX(${shift}px)`
        }, {
          transform: "none"
        }], RESIZE_ANIMATION));
      }
      return () => {
        for (const animation of animations) {
          animation?.cancel();
        }
      };
    };
    t6 = [width, shouldReduceMotion];
    $[5] = shouldReduceMotion;
    $[6] = width;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[9] !== className) {
    t7 = cn("relative flex w-full flex-col items-center justify-center gap-y-2 text-center text-3xl leading-none font-semibold tracking-tight sm:flex-row sm:items-baseline sm:gap-y-0 sm:text-4xl md:text-5xl", className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t10;
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      pausedRef.current = true;
    };
    t9 = () => {
      pausedRef.current = false;
    };
    t10 = <span ref={leadRef} className="whitespace-nowrap text-foreground/90">Inspect any domain’s</span>;
    $[11] = t10;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="sr-only">{HEADING_COMPLETION}</span>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== width) {
    t12 = {
      width
    };
    $[15] = width;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="absolute -inset-px *:absolute *:inset-y-0 *:border-ring/20 *:bg-muted/40 *:shadow-[0_1px_0_rgb(0_0_0/0.05)]"><span ref={capLeftRef} className="left-0 w-(--radius-lg) rounded-l-lg border border-r-0 sm:w-(--radius-md) sm:rounded-l-md" /><span ref={pillMidRef} className="inset-x-(--radius-lg) border-y sm:inset-x-(--radius-md)" /><span ref={capRightRef} className="right-0 w-(--radius-lg) rounded-r-lg border border-l-0 sm:w-(--radius-md) sm:rounded-r-md" /></span>;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = ROTATING_WORDS[index];
  let t15;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      y: "100%",
      opacity: 0
    };
    t16 = {
      y: 0,
      opacity: 1
    };
    $[18] = t15;
    $[19] = t16;
  } else {
    t15 = $[18];
    t16 = $[19];
  }
  let t17;
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      y: "-100%",
      opacity: 0,
      transition: {
        duration: DURATION_S * 0.6
      }
    };
    t18 = {
      type: "tween",
      ease: EASE,
      duration: DURATION_S
    };
    $[20] = t17;
    $[21] = t18;
  } else {
    t17 = $[20];
    t18 = $[21];
  }
  const t19 = ROTATING_WORDS[index];
  let t20;
  if ($[22] !== t14 || $[23] !== t19) {
    t20 = <span className="absolute left-1/2 grid -translate-x-1/2 justify-items-center *:col-start-1 *:row-start-1"><AnimatePresence initial={false}><motion.span key={t14} initial={t15} animate={t16} exit={t17} transition={t18} data-word={t19} className="inline-block will-change-[transform,opacity] before:content-[attr(data-word)]" /></AnimatePresence></span>;
    $[22] = t14;
    $[23] = t19;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const t21 = ROTATING_WORDS[index];
  let t22;
  if ($[25] !== t21) {
    t22 = <span data-word={t21} className="invisible select-none before:content-[attr(data-word)]" />;
    $[25] = t21;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t20 || $[28] !== t22) {
    t23 = <span className="relative flex h-[1.15em] w-full items-center overflow-x-visible overflow-y-clip whitespace-nowrap">{t20}{t22}</span>;
    $[27] = t20;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t12 || $[31] !== t23) {
    t24 = <span className="relative inline-flex items-center px-2 py-0.5 text-foreground sm:ml-2.5 sm:px-3 sm:py-1" style={t12} aria-hidden={true}>{t13}{t23}</span>;
    $[30] = t12;
    $[31] = t23;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  let t26;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span ref={measureRef} className="pointer-events-none invisible absolute top-0 left-0 flex h-0 flex-col items-start overflow-hidden" aria-hidden={true}>{ROTATING_WORDS.map(_temp3)}</span>;
    t26 = <span ref={trailRef} className="hidden whitespace-nowrap text-foreground/90 before:content-['.'] sm:inline-block" aria-hidden={true} />;
    $[33] = t25;
    $[34] = t26;
  } else {
    t25 = $[33];
    t26 = $[34];
  }
  let t27;
  if ($[35] !== t24 || $[36] !== t7) {
    t27 = <h1 ref={headingRef} className={t7} onPointerEnter={t8} onPointerLeave={t9}>{t10}{" "}{t11}{t24}{t25}{t26}</h1>;
    $[35] = t24;
    $[36] = t7;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  return t27;
}
function _temp3(word) {
  return <span key={word} data-word={word} className="px-2 py-0.5 whitespace-nowrap before:content-[attr(data-word)] sm:px-3 sm:py-1" />;
}
function _temp2(mirror) {
  return mirror.getBoundingClientRect().width;
}
function _temp(prev) {
  return (prev + 1) % ROTATING_WORDS.length;
}